import i18n from '../../../../i18n';
import { getModelNameIcon, getModelNameLabel } from '../../../helpers/ChatTypeHelpers';
import { MonitorEventLogResponse } from '../../../models/monitor/MonitorEventLogResponse';
import { isMonitorEventType, MonitorEventType } from '../../../models/monitor/MonitorEventType';
import { MonitorEventsResponse } from '../../../models/monitor/MonitorEventsResponse';
import { presetLabel } from '../../../models/projects/MonitoringInterval';

type EventFormatter = (args: Record<string, unknown> | null) => string;

type EventMapConfig = {
  // it is possible to set icon class for event like this:
  // iconClass: 'ico--info',
  readonly iconClass?: string;
  readonly labelFn: EventFormatter;
};

const EVENT_MAP: Record<MonitorEventType, EventMapConfig> = {
  PROMPT_ADDED: {
    labelFn: (args) => i18n.t('monitorEventPromptAdded', { prompt: String(args?.prompt ?? '') }),
  },
  PROMPT_DELETED: {
    labelFn: (args) => i18n.t('monitorEventPromptDeleted', { prompt: String(args?.prompt ?? '') }),
  },
  MODEL_ADDED: {
    labelFn: () => i18n.t('monitorEventModelAdded'),
  },
  MODEL_DELETED: {
    labelFn: () => i18n.t('monitorEventModelDeleted'),
  },
  MODEL_RELEASED: {
    labelFn: (args) => i18n.t('monitorEventModelReleased', { model: String(args?.model ?? '') }),
  },
  // Already netted per day by the backend: one marker, from the day's first schedule to its last.
  MONITORING_SCHEDULE_CHANGED: {
    labelFn: (args) =>
      i18n.t('monitorEventScheduleChanged', {
        from: presetLabel({ value: Number(args?.fromValue), unit: String(args?.fromUnit) as 'DAY' | 'MONTH' }),
        to: presetLabel({ value: Number(args?.toValue), unit: String(args?.toUnit) as 'DAY' | 'MONTH' }),
      }),
  },
};

const mapEventLogToMonitorEvent = (event: MonitorEventLogResponse): MonitorEventsResponse | null => {
  if (!isMonitorEventType(event.type)) {
    return null;
  }

  const eventMap = EVENT_MAP[event.type];

  const modelNameIconClass = getModelNameIcon(event.modelName);
  const modelNameLabel = modelNameIconClass ? getModelNameLabel(event.modelName) : null;

  return {
    created: new Date(event.created),
    type: event.type,
    label: eventMap.labelFn(event.args),
    ...(eventMap.iconClass ? { iconClass: eventMap.iconClass } : {}),
    ...(modelNameIconClass && modelNameLabel
      ? {
          model: {
            iconClass: modelNameIconClass,
            label: modelNameLabel,
          },
        }
      : {}),
  };
};

export const formatEventsForLineChart = (
  events: MonitorEventLogResponse[] | null | undefined
): MonitorEventsResponse[] => {
  if (!events || events.length === 0) {
    return [];
  }

  return events
    .map(mapEventLogToMonitorEvent)
    .filter((event): event is MonitorEventsResponse => event !== null);
};
